import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Upload, Pencil, Trash2, ArrowLeft, FileText, Star, ShoppingBag } from 'lucide-react';
import * as api from '../lib/api';
import { formatPrice } from '../lib/eventsFormat';
import { prepareImage, fmtSize } from '../lib/imageResize';

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const FILE_ACCEPT = '.pdf,.zip,.docx,.xlsx,.mp3,.m4a,.mp4,application/pdf';
const inputCls = 'w-full min-h-[44px] px-4 py-2.5 bg-white/70 dark:input-white-dark rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary text-text-main';

const Field = ({ id, label, hint, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-text-main mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
  </div>
);

const Segment = ({ label, value, options, onChange }) => (
  <div role="group" aria-label={label}>
    <p className="text-sm font-semibold text-text-main mb-1.5">{label}</p>
    <div className="flex gap-2 flex-wrap">
      {options.map(([val, text]) => (
        <button
          key={val} type="button" onClick={() => onChange(val)} aria-pressed={value === val}
          className={`min-h-[44px] px-4 rounded-full border text-sm font-semibold transition-colors ${value === val ? 'bg-primary text-white border-primary' : 'bg-white/50 dark:bg-white/5 border-teal/30 text-text-main hover:bg-teal/10'}`}
        >
          {text}
        </button>
      ))}
    </div>
  </div>
);

/** An upload control that resizes images, shows progress and puts errors right beside the button. */
const useUploader = () => {
  const [state, setState] = useState({ busy: false, error: '', note: '' });
  const run = async (task) => {
    setState({ busy: true, error: '', note: '' });
    try {
      const note = await task();
      setState({ busy: false, error: '', note: note || 'Saved.' });
    } catch (err) {
      setState({ busy: false, error: err?.response?.data?.detail || err?.message || 'Upload failed. Please try again.', note: '' });
    }
  };
  return [state, run];
};

const Status = ({ state }) => (
  <>
    {state.error && <p role="alert" className="text-sm text-red-600 mt-2">{state.error}</p>}
    {state.note && !state.error && <p role="status" className="text-sm text-green-700 mt-2">{state.note}</p>}
  </>
);

const ImageSlot = ({ label, hint, imageUrl, onUpload, onRemove, wide, testId }) => {
  const [state, run] = useUploader();
  const pick = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    run(async () => {
      const { file, note } = await prepareImage(picked, { maxDimension: wide ? 2000 : 1600 });
      const fd = new FormData();
      fd.append('file', file);
      await onUpload(fd);
      return note || 'Image updated.';
    });
  };
  return (
    <div className="p-4 rounded-arch bg-white/50 dark:bg-white/5" data-testid={testId}>
      <div className="flex items-center gap-4 flex-wrap">
        <div className={`${wide ? 'w-36 h-16' : 'w-28 h-[70px]'} rounded-xl bg-gradient-to-br from-teal to-lavender bg-cover bg-center shrink-0`} style={imageUrl ? { backgroundImage: `url("${imageUrl}")` } : undefined} aria-hidden="true" />
        <div className="flex-1 min-w-[160px]">
          <p className="font-heading font-semibold text-text-main">{label}</p>
          <p className="text-xs text-text-muted">{imageUrl ? 'Custom image set' : 'Standard look'}</p>
        </div>
        <label className="min-h-[44px] px-4 rounded-full border border-teal/30 text-sm font-semibold inline-flex items-center gap-2 cursor-pointer hover:bg-teal/10 focus-within:ring-2 focus-within:ring-primary">
          <Upload className="w-4 h-4" aria-hidden="true" />{state.busy ? 'Uploading...' : imageUrl ? 'Change image' : 'Choose image'}
          <input type="file" accept="image/*,.heic,.heif" onChange={pick} disabled={state.busy} className="sr-only" />
        </label>
        {imageUrl && onRemove && (
          <button type="button" onClick={() => run(async () => { await onRemove(); return 'Back to the standard look.'; })} className="min-h-[44px] px-4 rounded-full text-sm font-semibold text-red-600 hover:bg-red-50">Remove</button>
        )}
      </div>
      {hint && <p className="text-xs text-text-muted mt-2">{hint}</p>}
      <Status state={state} />
    </div>
  );
};

const BLANK = { title: '', summary: '', description: '', kind: 'file', access: 'free', price: '', status: 'draft', link_url: '', featured: false, sort_order: 0 };
const fromItem = (it) => ({ ...BLANK, ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, it[k] ?? BLANK[k]])), price: it.price_cents ? (it.price_cents / 100).toFixed(2) : '', link_url: it.link_url || '', summary: it.summary || '', description: it.description || '' });
const toPayload = (f) => {
  const price = parseFloat(f.price);
  return {
    title: f.title.trim(), summary: f.summary.trim() || null, description: f.description.trim() || null, kind: f.kind,
    access: f.access, price_cents: f.access === 'paid' && Number.isFinite(price) ? Math.round(price * 100) : 0,
    status: f.status, link_url: f.kind === 'link' ? (f.link_url.trim() || null) : null, featured: !!f.featured,
    sort_order: parseInt(f.sort_order, 10) || 0,
  };
};

const ItemForm = ({ initial, onSaved, onCancel, flash }) => {
  const editing = !!initial?.id;
  const [f, setF] = useState(initial ? fromItem(initial) : BLANK);
  const [item, setItem] = useState(initial || null);
  const [saving, setSaving] = useState(false);
  const [fileState, runFile] = useUploader();
  const [progress, setProgress] = useState(null);
  const set = (k) => (e) => setF((cur) => ({ ...cur, [k]: e.target.value }));
  const setVal = (k, v) => setF((cur) => ({ ...cur, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { flash('error', 'A title is required'); return; }
    setSaving(true);
    try {
      const payload = toPayload(f);
      const res = item?.id ? await api.adminUpdateLibraryItem(item.id, payload) : await api.adminCreateLibraryItem(payload);
      setItem((cur) => ({ ...(cur || {}), ...res.data }));
      flash('success', item?.id ? 'Item saved' : 'Item created. You can now add the file and cover.');
      onSaved(res.data, !item?.id);
    } catch (err) {
      flash('error', err?.response?.data?.detail || 'Could not save the item');
    } finally { setSaving(false); }
  };

  const pickFile = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked || !item?.id) return;
    if (picked.size > MAX_FILE_BYTES) { runFile(async () => { throw new Error(`That file is ${fmtSize(picked.size)}. The limit is 50 MB.`); }); return; }
    runFile(async () => {
      const fd = new FormData();
      fd.append('file', picked);
      const res = await api.adminUploadLibraryFile(item.id, fd, (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : null));
      setProgress(null);
      setItem((cur) => ({ ...cur, has_file: true, file_name: res.data.file_name, file_size: res.data.file_size }));
      return `Uploaded ${res.data.file_name} (${fmtSize(res.data.file_size)}).`;
    }).finally(() => setProgress(null));
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="library-form">
      <button type="button" onClick={onCancel} className="min-h-[44px] inline-flex items-center gap-1 text-primary font-semibold"><ArrowLeft className="w-4 h-4" aria-hidden="true" />All items</button>
      <h2 className="font-heading text-xl font-semibold text-text-main">{editing ? 'Edit item' : 'New item'}</h2>

      <Field id="li-title" label="Title"><input id="li-title" className={inputCls} value={f.title} onChange={set('title')} maxLength={120} required /></Field>
      <Field id="li-summary" label="Short description (shown on the card)" hint="One or two lines."><input id="li-summary" className={inputCls} value={f.summary} onChange={set('summary')} maxLength={180} /></Field>
      <Field id="li-desc" label="Longer description (optional)"><textarea id="li-desc" className={`${inputCls} min-h-[90px]`} value={f.description} onChange={set('description')} maxLength={3000} /></Field>

      <Segment label="Type" value={f.kind} onChange={(v) => setVal('kind', v)} options={[['file', 'Downloadable file'], ['link', 'Link or tool']]} />
      <Segment label="Who gets it" value={f.access} onChange={(v) => setVal('access', v)} options={[['free', 'Free for members'], ['plus', 'Sirati+ perk'], ['paid', 'One-time purchase']]} />
      {f.access === 'plus' && <p className="text-xs text-text-muted -mt-2">Sirati+ perks are included with every paid plan and cannot be bought separately.</p>}
      {f.access === 'paid' && (
        <>
          <Field id="li-price" label="Price (USD)" hint="Sirati+ monthly and yearly members pay half. Lifetime members get it included."><input id="li-price" inputMode="decimal" className={inputCls} value={f.price} onChange={set('price')} placeholder="24.00" /></Field>
          <p className="text-xs text-text-muted -mt-2">Members pay once and keep it. Lifetime members get it included. Refund policy: non-refundable once downloaded, full refund within 7 days if never downloaded.</p>
        </>
      )}
      {f.kind === 'link' && <Field id="li-link" label="Link" hint="Starts with https://, or / for a page inside Sirati."><input id="li-link" className={inputCls} value={f.link_url} onChange={set('link_url')} placeholder="https://" /></Field>}

      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="li-status" label="Status" hint="Draft is hidden from members. Coming soon shows a card with no download.">
          <select id="li-status" className={inputCls} value={f.status} onChange={set('status')}>
            <option value="draft">Draft</option><option value="published">Published</option><option value="coming_soon">Coming soon</option>
          </select>
        </Field>
        <Field id="li-sort" label="Order" hint="Lower numbers first."><input id="li-sort" inputMode="numeric" className={inputCls} value={f.sort_order} onChange={set('sort_order')} /></Field>
      </div>
      <label htmlFor="li-featured" className="flex items-center gap-3 text-sm font-semibold text-text-main cursor-pointer">
        <input id="li-featured" type="checkbox" checked={f.featured} onChange={(e) => setVal('featured', e.target.checked)} className="w-5 h-5 accent-[#4AADA8]" />
        Feature this item at the top (shown large)
      </label>

      <div className="flex gap-3 flex-wrap">
        <button type="submit" disabled={saving} className="min-h-[48px] px-7 rounded-full bg-primary text-white font-bold btn-hover disabled:opacity-50" data-testid="save-library-item">{saving ? 'Saving...' : 'Save item'}</button>
        <button type="button" onClick={onCancel} className="min-h-[48px] px-6 rounded-full border-2 border-teal/40 text-primary font-bold hover:bg-teal/10">Cancel</button>
      </div>

      <hr className="border-teal/20" />
      {!item?.id ? (
        <p className="text-sm text-text-muted">Save the item first, then add its file and cover image.</p>
      ) : (
        <div className="space-y-5">
          {f.kind === 'file' && (
            <div>
              <p className="text-sm font-semibold text-text-main mb-1.5">File</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 text-sm text-text-main"><FileText className="w-4 h-4 text-primary" aria-hidden="true" />{item.has_file ? `${item.file_name || 'File uploaded'}${item.file_size ? ` (${fmtSize(item.file_size)})` : ''}` : 'No file yet'}</span>
                <label className="min-h-[44px] px-4 rounded-full border border-teal/30 text-sm font-semibold inline-flex items-center gap-2 cursor-pointer hover:bg-teal/10 focus-within:ring-2 focus-within:ring-primary">
                  <Upload className="w-4 h-4" aria-hidden="true" />{fileState.busy ? (progress !== null ? `Uploading ${progress}%` : 'Uploading...') : item.has_file ? 'Replace file' : 'Upload file'}
                  <input type="file" accept={FILE_ACCEPT} onChange={pickFile} disabled={fileState.busy} className="sr-only" aria-describedby="li-file-help" />
                </label>
              </div>
              <p id="li-file-help" className="text-xs text-text-muted mt-1.5">PDF, ZIP, Word, Excel, MP3, M4A or MP4, up to 50 MB. It is stored privately: members get a download link that expires after one minute. Long videos are better hosted on a video service and added as a link.</p>
              <Status state={fileState} />
            </div>
          )}
          <ImageSlot
            label="Cover image" testId="library-cover-slot" imageUrl={item.cover_url}
            hint="About 1200 x 800 px. Any photo works, it is resized for you. Without one, the card shows soft arches."
            onUpload={async (fd) => { const res = await api.adminUploadLibraryCover(item.id, fd); setItem((cur) => ({ ...cur, cover_url: res.data.url })); }}
          />
        </div>
      )}
    </form>
  );
};

const Purchases = ({ item, onBack, flash }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.adminGetLibraryPurchases(item.id).then((r) => setRows(r.data || [])).catch(() => flash('error', 'Could not load purchases'));
  }, [item.id, flash]);
  useEffect(() => { load(); }, [load]);

  const refund = async (row) => {
    if (!window.confirm(`Refund ${row.member || 'this member'} $${(row.amount_cents / 100).toFixed(2)} for "${item.title}"? They lose access to it.`)) return;
    try {
      await api.adminRefundLibraryPurchase(row.id, false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && detail.startsWith('Outside the refund policy')) {
        if (!window.confirm(`${detail}\n\nRefund anyway?`)) return;
        try { await api.adminRefundLibraryPurchase(row.id, true); } catch (e2) { flash('error', e2?.response?.data?.detail || 'Refund failed'); return; }
      } else { flash('error', detail || 'Refund failed'); return; }
    }
    flash('success', 'Refunded');
    load();
  };

  const paidRows = (rows || []).filter((r) => r.status === 'paid');
  return (
    <div data-testid="library-purchases">
      <button onClick={onBack} className="min-h-[44px] inline-flex items-center gap-1 text-primary font-semibold"><ArrowLeft className="w-4 h-4" aria-hidden="true" />All items</button>
      <h2 className="font-heading text-xl font-semibold text-text-main mt-1">{item.title}</h2>
      <p className="text-sm text-text-muted mb-4">{paidRows.length} purchase{paidRows.length === 1 ? '' : 's'} &middot; policy: non-refundable once downloaded, full refund within 7 days if never downloaded</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-text-muted"><th className="py-2 pr-3">Member</th><th className="pr-3">Paid</th><th className="pr-3">Downloaded</th><th className="pr-3">Status</th><th /></tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan="5" className="py-4 text-text-muted">Loading...</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan="5" className="py-4 text-text-muted">No purchases yet.</td></tr>}
            {(rows || []).map((r) => (
              <tr key={r.id} className="border-t border-primary/15" data-testid={`purchase-${r.id}`}>
                <td className="py-2.5 pr-3"><span className="font-medium text-text-main">{r.member}</span><br /><span className="text-xs text-text-muted">{r.email}</span></td>
                <td className="pr-3">${(r.amount_cents / 100).toFixed(2)}{r.discount_pct ? ` (${r.discount_pct}% off)` : ''}</td>
                <td className="pr-3">{r.downloaded ? 'Yes' : 'No'}</td>
                <td className="pr-3 capitalize">{r.status}</td>
                <td className="text-right">
                  {r.status === 'paid' && (
                    <button onClick={() => refund(r)} title={r.refund_note} className="min-h-[44px] px-3 text-red-600 font-semibold hover:underline" data-testid={`refund-purchase-${r.id}`}>
                      {r.refund_eligible ? 'Refund' : 'Refund (outside policy)'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LibraryAdmin = () => {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState({ view: 'list' });
  const [enabled, setEnabled] = useState(null);
  const [msg, setMsg] = useState(null);
  const flash = useCallback((type, text) => setMsg({ type, text }), []);

  const load = useCallback(() => {
    api.adminGetLibrary().then((r) => setData(r.data)).catch(() => flash('error', 'Could not load the Library'));
  }, [flash]);
  useEffect(() => {
    load();
    api.getLibrarySetting().then((r) => setEnabled(!!r.data?.enabled)).catch(() => setEnabled(false));
  }, [load]);

  const toggleEnabled = async () => {
    try {
      const res = await api.setAdminSetting('library_enabled', !enabled);
      setEnabled(res.data?.value === true || res.data?.value === 'true');
    } catch { flash('error', 'Could not change the setting'); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}" and its file? This cannot be undone.`)) return;
    try { await api.adminDeleteLibraryItem(it.id); flash('success', 'Deleted'); load(); }
    catch (e) { flash('error', e?.response?.data?.detail || 'Could not delete'); }
  };

  const items = data?.items || [];
  const CHIP = { published: 'bg-teal/20 text-teal-dark', draft: 'bg-olive/15 text-olive', coming_soon: 'bg-lavender/30 text-lavender-dark' };
  const CHIP_TEXT = { published: 'Published', draft: 'Draft', coming_soon: 'Coming soon' };
  const ACCESS_TEXT = (it) => (it.access === 'free' ? 'Free' : it.access === 'plus' ? 'Sirati+ perk' : `Paid ${formatPrice(it.price_cents)}`);

  return (
    <div data-testid="library-admin">
      {msg && (
        <div role="status" className={`mb-4 px-4 py-3 rounded-arch flex items-center justify-between ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {msg.text}<button onClick={() => setMsg(null)} aria-label="Dismiss message" className="min-h-[44px] min-w-[44px] text-lg leading-none">&times;</button>
        </div>
      )}

      {mode.view === 'list' && (
        <>
          <div className="flex items-center justify-between gap-4 p-4 mb-5 rounded-arch border border-white/20 bg-white/40 dark:bg-white/10" data-testid="library-switch-card">
            <div>
              <p className="font-heading font-semibold text-text-main">The Library is {enabled ? 'visible to members' : 'hidden from members'}</p>
              <p className="text-xs text-text-muted">While hidden, only admins see the Library page and its menu links.</p>
            </div>
            <button role="switch" aria-checked={enabled === true} aria-label="Show the Library to members" disabled={enabled === null} onClick={toggleEnabled} data-testid="library-switch"
              className={`w-14 h-8 rounded-full relative transition-colors shrink-0 ${enabled ? 'bg-primary' : 'bg-olive/30'}`}>
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${enabled ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

          <h2 className="font-heading text-lg font-semibold text-text-main mb-2">Page banner</h2>
          <ImageSlot wide testId="library-banner-slot" label="Library banner" imageUrl={data?.banner_url}
            hint="A wide image, about 2000 x 400 px. Keep the left side calm: the title and text sit there."
            onUpload={async (fd) => { await api.adminUploadLibraryBanner(fd); load(); }} />

          <h2 className="font-heading text-lg font-semibold text-text-main mt-6 mb-2">Library card backgrounds</h2>
          <div className="grid gap-3">
            <ImageSlot testId="library-card-plus-slot" label="Sirati+ card" imageUrl={data?.card_images?.plus}
              hint="About 1200 x 760 px. Used for the member Library card when it launches. Free members keep the standard card."
              onUpload={async (fd) => { await api.adminUploadLibraryCard('plus', fd); load(); }}
              onRemove={async () => { await api.adminRemoveLibraryCard('plus'); load(); }} />
            <ImageSlot testId="library-card-lifetime-slot" label="Lifetime card" imageUrl={data?.card_images?.lifetime}
              hint="Same size. The Lifetime card keeps its shimmering reflection on top of the image."
              onUpload={async (fd) => { await api.adminUploadLibraryCard('lifetime', fd); load(); }}
              onRemove={async () => { await api.adminRemoveLibraryCard('lifetime'); load(); }} />
          </div>

          <div className="flex items-center justify-between mt-8 mb-3">
            <h2 className="font-heading text-xl font-semibold text-text-main">Items</h2>
            <button onClick={() => setMode({ view: 'form', item: null })} className="min-h-[44px] px-5 rounded-full bg-primary text-white font-bold inline-flex items-center gap-2 btn-hover" data-testid="new-library-item-btn">
              <Plus className="w-4 h-4" aria-hidden="true" />New item
            </button>
          </div>
          {data === null && <p className="text-text-muted">Loading...</p>}
          {data && items.length === 0 && <p className="text-text-muted">No items yet. Add your first one.</p>}
          <ul className="space-y-3">
            {items.map((it) => (
              <li key={it.id} className="p-4 rounded-arch bg-white/50 dark:bg-white/5 border border-teal/15 flex items-center gap-4 flex-wrap" data-testid={`admin-library-${it.id}`}>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal to-lavender bg-cover bg-center shrink-0" style={it.cover_url ? { backgroundImage: `url("${it.cover_url}")` } : undefined} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold text-text-main">{it.title}<span className={`ml-2 px-2.5 py-0.5 rounded-full text-[11.5px] font-bold ${CHIP[it.status]}`}>{CHIP_TEXT[it.status]}</span>{it.featured && <Star className="inline w-3.5 h-3.5 ml-2 text-lavender-dark" aria-label="Featured" />}</p>
                  <p className="text-[13px] text-text-muted">
                    {ACCESS_TEXT(it)} &middot; {it.kind === 'link' ? 'Link' : it.has_file ? `${it.file_name || 'File'} ${it.file_size ? `(${fmtSize(it.file_size)})` : ''}` : 'No file yet'} &middot; {it.downloads} download{it.downloads === 1 ? '' : 's'}{it.purchases ? ` · ${it.purchases} bought` : ''}
                  </p>
                </div>
                <div className="flex gap-1">
                  {it.access === 'paid' && (
                    <button onClick={() => setMode({ view: 'purchases', item: it })} className="min-h-[44px] px-3 rounded-full text-sm font-semibold text-primary hover:bg-teal/10 inline-flex items-center gap-1.5" data-testid={`purchases-library-${it.id}`}><ShoppingBag className="w-4 h-4" aria-hidden="true" />Purchases</button>
                  )}
                  <button onClick={() => setMode({ view: 'form', item: it })} className="min-h-[44px] px-3 rounded-full text-sm font-semibold text-primary hover:bg-teal/10 inline-flex items-center gap-1.5" data-testid={`edit-library-${it.id}`}><Pencil className="w-4 h-4" aria-hidden="true" />Edit</button>
                  <button onClick={() => remove(it)} aria-label={`Delete ${it.title}`} className="min-h-[44px] min-w-[44px] rounded-full text-red-600 hover:bg-red-50 inline-flex items-center justify-center"><Trash2 className="w-4 h-4" aria-hidden="true" /></button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {mode.view === 'purchases' && <Purchases item={mode.item} flash={flash} onBack={() => { setMode({ view: 'list' }); load(); }} />}

      {mode.view === 'form' && (
        <ItemForm
          key={mode.item?.id || 'new'} initial={mode.item} flash={flash}
          onCancel={() => { setMode({ view: 'list' }); load(); }}
          onSaved={(saved, wasNew) => { load(); if (wasNew) setMode({ view: 'form', item: saved }); }}
        />
      )}
    </div>
  );
};

export default LibraryAdmin;
